import React, { useCallback, useEffect, useState } from 'react';
import { api } from 'lib/api';
import { API_ENDPOINTS } from 'constants/endpoints';
import { usePopup } from 'context/PopupProvider';
import { Plus, Trash2, Edit2, Table2, X, RefreshCw } from 'lucide-react';
import CursorPagination from 'components/CursorPagination';
import { appendCursorParams, parseCursorPagination } from 'lib/cursor-pagination';
import { useCursorListPage } from 'hooks/useCursorListPage';
import { DEFAULT_PAGE_SIZE } from 'lib/page-size-options';
import { listRowNumber, LIST_TD_NO_CLASS, LIST_TH_NO_CLASS, LIST_TABLE_CLASS, LIST_THEAD_CLASS, LIST_TBODY_CLASS, LIST_TH_CLASS, LIST_TD_CLASS } from 'lib/list-table';
import { deriveMaxLimitFromBoot, derivePotLimitFromBoot } from 'lib/table-stakes';
import type { TableProfile, GameVariantOption } from 'types/admin';

type TableTimeoutAction = 'auto_pack' | 'auto_blind' | 'auto_chaal';

type TableFormState = {
  name: string;
  max_seats: number;
  boot_amount: number;
  min_bet: number;
  max_bet: number;
  min_buy_in: number;
  shar_amount: number;
  max_blind_rounds: number;
  timeout_action: TableTimeoutAction;
  game_variant_id: number;
  status: boolean;
};

const defaultTableForm = (): TableFormState => {
  const boot_amount = 10;
  return {
    name: '',
    max_seats: 5,
    boot_amount,
    min_bet: 10,
    max_bet: deriveMaxLimitFromBoot(boot_amount),
    min_buy_in: 1000,
    shar_amount: 0,
    max_blind_rounds: 4,
    timeout_action: 'auto_pack',
    game_variant_id: 0,
    status: true,
  };
};

const applyBootDerivedLimits = (form: TableFormState, bootAmount: number): TableFormState => ({
  ...form,
  boot_amount: bootAmount,
  max_bet: deriveMaxLimitFromBoot(bootAmount),
});

const formatTableAmount = (value: number | string | null | undefined): string =>
  Number(value ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const TableManagement = () => {
  const { confirm } = usePopup();
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  // Selectable game variants are DB-driven — fetched, never hardcoded.
  const [variants, setVariants] = useState<GameVariantOption[]>([]);
  const [editingTableId, setEditingTableId] = useState<number | null>(null);
  // Variant the edited table currently uses. Kept so the dropdown can still show it
  // even when it is no longer in the active-variants list (deactivated/unavailable).
  const [editingVariant, setEditingVariant] = useState<{ id: number; name: string | null; active: boolean } | null>(null);

  // Create / Edit Form State. game_variant_id is a game_variants(id); status is
  // BOOLEAN (table_profiles.status). 0 = no variant selected yet.
  const [form, setForm] = useState<TableFormState>(defaultTableForm());

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [error, setError] = useState('');

  const isEditMode = editingTableId !== null;

  const fetchPage = useCallback(async (cursor: number | null) => {
    const params = appendCursorParams(new URLSearchParams(), cursor, pageSize);
    const res = await api.get<TableProfile[]>(`${API_ENDPOINTS.ADMIN.TABLES.GET_ALL}?${params.toString()}`);
    const meta = parseCursorPagination(res);
    return {
      items: res.success && Array.isArray(res.data) ? res.data : [],
      nextCursor: meta.nextCursor,
      hasMore: meta.hasMore,
      total: meta.total,
      totalPages: meta.totalPages,
    };
  }, [pageSize]);

  const {
    page,
    items: tables,
    loading,
    refreshing,
    hasMore,
    total,
    totalPages,
    limit,
    resetAndLoad,
    refreshCurrent,
    goFirst,
    goToPage,
    goLast,
  } = useCursorListPage(fetchPage, pageSize);

  useEffect(() => {
    resetAndLoad();
  }, [pageSize, resetAndLoad]);

  const fetchTableVariants = () => {
    api.get<GameVariantOption[]>(API_ENDPOINTS.ADMIN.TABLE_VARIANTS.GET_ALL)
      .then(res => {
        if (res.success && res.data) {
          setVariants(res.data);
        }
      })
      .catch(err => {
        console.error('Failed to fetch table variants:', err);
      });
  };

  useEffect(() => {
    fetchTableVariants();
  }, []);

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingTableId(null);
    setEditingVariant(null);
    setError('');
    setForm(defaultTableForm());
  };

  const handleCreateInit = () => {
    setEditingTableId(null);
    setEditingVariant(null);
    setForm({ ...defaultTableForm(), game_variant_id: variants[0]?.id ?? 0 });
    setError('');
    setIsModalOpen(true);
  };

  const handleEditInit = (table: TableProfile) => {
    setEditingTableId(table.id);
    // Remember the table's current variant so the dropdown can display it even if it
    // has since been deactivated (and thus dropped from the active-variants list).
    setEditingVariant(
      table.game_variant_id
        ? { id: table.game_variant_id, name: table.variant_name ?? null, active: table.variant_status !== false }
        : null
    );
    setForm({
      name: table.name,
      max_seats: table.max_seats,
      boot_amount: Number(table.boot_amount),
      min_bet: Number(table.min_bet ?? 0),
      max_bet: deriveMaxLimitFromBoot(Number(table.boot_amount)),
      min_buy_in: Number(table.min_buy_in ?? table.boot_amount ?? 0),
      shar_amount: Number(table.shar_amount ?? 0),
      max_blind_rounds: table.max_blind_rounds,
      timeout_action: (table.timeout_action ?? 'auto_pack') as TableTimeoutAction,
      game_variant_id: table.game_variant_id ?? 0,
      status: table.status
    });
    setError('');
    setIsModalOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!form.game_variant_id) {
      setError('Please select a game variant.');
      return;
    }

    if (Number(form.min_bet) > Number(form.max_bet)) {
      setError('Min Bet must be less than or equal to Max Limit.');
      return;
    }

    if (Number(form.min_buy_in) < Number(form.boot_amount)) {
      setError('Min Buy-in must be at least the Boot Amount.');
      return;
    }

    const request = editingTableId !== null
      ? api.put(API_ENDPOINTS.ADMIN.TABLES.UPDATE(editingTableId), form)
      : api.post(API_ENDPOINTS.ADMIN.TABLES.CREATE, form);

    request
      .then(res => {
        if (res.success) {
          resetAndLoad();
          closeModal();
        } else {
          // The backend rejects a duplicate mode + stakes combination with a
          // "Resource already exists" message — surface it verbatim when present.
          setError(
            res.message ||
              (editingTableId !== null
                ? 'Failed to update table.'
                : 'Failed to create table. Please check input parameters.')
          );
        }
      })
      .catch(err => {
        console.error('Save error:', err);
        setError(
          editingTableId !== null
            ? 'Error updating table profile.'
            : 'Error creating table profile.'
        );
      });
  };

  const handleDelete = async (id: number) => {
    const confirmed = await confirm({
      title: 'Delete Table Profile',
      message: 'Are you sure you want to delete this table profile? This action cannot be undone.',
      variant: 'danger',
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
    });
    if (!confirmed) return;
    api.delete(API_ENDPOINTS.ADMIN.TABLES.DELETE(id))
      .then(res => {
        if (res.success) {
          resetAndLoad();
        }
      })
      .catch(err => {
        console.error('Delete error:', err);
      });
  };

  return (
    <div className="space-y-8 animate-fadeIn">
      {/* Header Banner */}
      <div className="bg-card p-6 rounded-2xl border border-line flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-blue-500/10 text-heading rounded-xl">
            <Table2 size={24} />
          </div>
          <div>
            <h3 className="text-xl font-bold text-heading">Table Configuration</h3>
            <p className="text-sm text-muted">Configure public stakes tiers, seat capacities, and dynamic scalability bounds.</p>
          </div>
        </div>
        <button
          onClick={handleCreateInit}
          className="flex items-center justify-center gap-2 px-4 py-2.5 bg-brand-gold hover:bg-opacity-95 text-brand-dark font-bold rounded-xl transition-all shadow-sm text-sm"
        >
          <Plus size={18} /> Create New Table Profile
        </button>
      </div>

      {error && (
        <div className="bg-red-500/10 text-brand-danger border border-red-100 px-4 py-3 rounded-xl text-sm font-semibold flex items-center gap-2">
          {error}
        </div>
      )}

      {/* Main List */}
      <div className="bg-card rounded-2xl shadow-sm border border-line overflow-hidden">
        <div className={`${LIST_TD_CLASS} border-b border-line flex items-center justify-between`}>
          <h4 className="font-bold text-heading">Stakes & Thresholds Ledger</h4>
          <div className="flex items-center gap-2">
            <span className="text-xs bg-subtle text-muted px-2.5 py-1 rounded-full font-semibold">
              {total.toLocaleString()} total · Page {page} of {totalPages}
            </span>
            <button
              type="button"
              onClick={() => refreshCurrent()}
              disabled={loading || refreshing}
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-line text-muted hover:text-heading hover:bg-subtle transition-colors disabled:opacity-50"
              title="Refresh"
            >
              <RefreshCw size={14} className={loading || refreshing ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>

        {loading && tables.length === 0 ? (
          <div className="flex h-48 items-center justify-center text-muted font-medium">
            <RefreshCw className="animate-spin mr-2" size={18} /> Loading table profiles...
          </div>
        ) : (
          <>
        <div className="overflow-x-auto">
          <table className={LIST_TABLE_CLASS}>
            <thead className={LIST_THEAD_CLASS}>
              <tr>
                <th className={`${LIST_TH_NO_CLASS} px-6 py-4`}>No.</th>
                <th className={LIST_TH_CLASS}>Table Name</th>
                <th className={LIST_TH_CLASS}>Game Variant</th>
                <th className={LIST_TH_CLASS}>Max Seats</th>
                <th className={LIST_TH_CLASS}>Boot Amount</th>
                <th className={LIST_TH_CLASS}>Min Buy-in</th>
                <th className={LIST_TH_CLASS}>Shar</th>
                <th className={LIST_TH_CLASS}>Pot Limit</th>
                <th className={LIST_TH_CLASS}>Max Blind Rounds</th>
                <th className={LIST_TH_CLASS}>Status</th>
                <th className={LIST_TH_CLASS}>Actions</th>
              </tr>
            </thead>
            <tbody className={LIST_TBODY_CLASS}>
              {tables.map((table, index) => (
                <tr key={table.id} className="hover:bg-subtle/50 transition-colors">
                  <td className={`${LIST_TD_NO_CLASS} px-6 py-4`}>{listRowNumber(page, index, limit)}</td>
                  {/* Name */}
                  <td className={`${LIST_TD_CLASS} whitespace-nowrap text-sm font-semibold text-heading`}>
                    {table.name}
                  </td>
                  {/* Game Variant */}
                  <td className={`${LIST_TD_CLASS} whitespace-nowrap`}>
                    <span className="px-2.5 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-brand-gold/10 text-brand-gold border border-brand-gold/20">
                      {table.variant_name ?? variants.find((v) => v.id === table.game_variant_id)?.name ?? '—'}
                    </span>
                    {table.variant_status === false && (
                      <span className="ml-2 px-2 py-0.5 inline-flex text-[10px] leading-5 font-bold uppercase rounded-full bg-red-500/10 text-red-700 border border-red-200"
                        title="This table's game variant is inactive">
                        Inactive
                      </span>
                    )}
                  </td>
                  {/* Max Seats */}
                  <td className={`${LIST_TD_CLASS} whitespace-nowrap text-sm text-body`}>
                    {`${table.max_seats} Seats`}
                  </td>
                  {/* Boot Amount */}
                  <td className={`${LIST_TD_CLASS} whitespace-nowrap text-sm font-semibold text-heading tabular-nums`}>
                    {formatTableAmount(table.boot_amount)}
                  </td>
                  <td className={`${LIST_TD_CLASS} whitespace-nowrap text-sm font-semibold text-heading tabular-nums`}>
                    {formatTableAmount(table.min_buy_in)}
                  </td>
                  <td className={`${LIST_TD_CLASS} whitespace-nowrap text-sm font-semibold text-heading tabular-nums`}>
                    {Number(table.shar_amount ?? 0) > 0 ? formatTableAmount(table.shar_amount) : '—'}
                  </td>
                  {/* Pot Limit */}
                  <td className={`${LIST_TD_CLASS} whitespace-nowrap text-sm font-semibold text-heading tabular-nums`}>
                    {formatTableAmount(derivePotLimitFromBoot(Number(table.boot_amount)))}
                  </td>
                  {/* Max Blind Rounds */}
                  <td className={`${LIST_TD_CLASS} whitespace-nowrap text-sm text-body`}>
                    {`${table.max_blind_rounds} Rounds`}
                  </td>
                  {/* Status */}
                  <td className={`${LIST_TD_CLASS} whitespace-nowrap`}>
                    <span className={`px-2.5 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full ${table.status ? 'bg-green-500/10 text-green-700 border border-green-200' : 'bg-red-500/10 text-red-700 border border-red-200'}`}>
                      {table.status ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  {/* Actions */}
                  <td className={`${LIST_TD_CLASS} whitespace-nowrap text-sm font-medium flex gap-3`}>
                    <button
                      onClick={() => handleEditInit(table)}
                      className="p-1 text-blue-600 hover:bg-blue-500/10 rounded"
                      title="Edit Table Profile"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(table.id)}
                      className="p-1 text-brand-danger hover:bg-red-500/10 rounded"
                      title="Delete Table Profile"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <CursorPagination
          page={page}
          totalPages={totalPages}
          total={total}
          itemCount={tables.length}
          limit={limit}
          hasMore={hasMore}
          loading={loading || refreshing}
          onFirst={goFirst}
          onPageChange={goToPage}
          onLast={goLast}
          onLimitChange={setPageSize}
        />
          </>
        )}
      </div>

      {/* Create / Edit Table Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-brand-dark/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 backdrop-blur-sm animate-fadeIn">
          <div className="bg-card w-full max-w-md max-h-[min(92dvh,720px)] sm:rounded-2xl rounded-t-2xl shadow-xl overflow-hidden animate-scaleIn border border-line flex flex-col">
            <div className="p-6 border-b border-line flex items-center justify-between bg-gradient-to-r from-brand-dark to-slate-900 text-white shrink-0">
              <div>
                <h4 className="font-bold text-lg text-brand-gold">
                  {isEditMode ? 'Edit Table Profile' : 'Create Table Profile'}
                </h4>
                <p className="text-xs text-faint">Set stakes, blind rounds & seat capacity</p>
              </div>
              <button
                onClick={closeModal}
                className="text-muted hover:text-white transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto overscroll-contain p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold text-muted uppercase tracking-wider mb-2">Table Profile Name</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Bronze Tier Room"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="block w-full rounded-lg border-line shadow-sm focus:border-brand-gold focus:ring-brand-gold sm:text-sm p-3 border"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-muted uppercase tracking-wider mb-2">Game Variant</label>
                <select
                  value={form.game_variant_id}
                  onChange={(e) => setForm({ ...form, game_variant_id: Number(e.target.value) })}
                  className="block w-full rounded-lg border-line shadow-sm focus:border-brand-gold focus:ring-brand-gold sm:text-sm p-3 border"
                >
                  {variants.length === 0 && <option value={0}>No game variants available</option>}
                  {/* When editing a table whose variant is no longer active, it isn't in the
                      active-variants list — surface it here so the current mode is still shown. */}
                  {editingVariant && !variants.some((v) => v.id === editingVariant.id) && (
                    <option value={editingVariant.id}>
                      {(editingVariant.name ?? `Variant #${editingVariant.id}`)} {editingVariant.active ? '(Unavailable)' : '(Inactive)'}
                    </option>
                  )}
                  {variants.map((variant) => (
                    <option key={variant.id} value={variant.id}>{variant.name}</option>
                  ))}
                </select>
                {editingVariant && !variants.some((v) => v.id === editingVariant.id) ? (
                  <p className="text-[11px] text-amber-700 mt-1 font-semibold">
                    This table uses “{editingVariant.name ?? `Variant #${editingVariant.id}`}”, which is currently {editingVariant.active ? 'unavailable' : 'inactive'}. Pick an active variant to change it, or re-activate that variant in the Variants module.
                  </p>
                ) : (
                  <p className="text-[11px] text-muted mt-1">
                    {variants.find((v) => v.id === form.game_variant_id)?.description}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-muted uppercase tracking-wider mb-2">Max Seats</label>
                  <select
                    value={form.max_seats}
                    onChange={(e) => setForm({ ...form, max_seats: Number(e.target.value) })}
                    className="block w-full rounded-lg border-line shadow-sm focus:border-brand-gold focus:ring-brand-gold sm:text-sm p-3 border"
                  >
                    <option value={5}>5 Seater</option>
                    <option value={7}>7 Seater</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-muted uppercase tracking-wider mb-2">Boot Amount ($)</label>
                  <input
                    type="number"
                    required
                    min={0.01}
                    step={0.01}
                    value={form.boot_amount}
                    onChange={(e) => setForm((prev) => applyBootDerivedLimits(prev, Number(e.target.value)))}
                    className="block w-full rounded-lg border-line shadow-sm focus:border-brand-gold focus:ring-brand-gold sm:text-sm p-3 border"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-muted uppercase tracking-wider mb-2">Min Bet ($)</label>
                  <input
                    type="number"
                    required
                    min={0}
                    max={form.max_bet}
                    step={0.01}
                    value={form.min_bet}
                    onChange={(e) => setForm({ ...form, min_bet: Number(e.target.value) })}
                    className="block w-full rounded-lg border-line shadow-sm focus:border-brand-gold focus:ring-brand-gold sm:text-sm p-3 border"
                  />
                  <p className="text-[11px] text-muted mt-1">Minimum chaal per turn</p>
                </div>

                <div>
                  <label className="block text-xs font-bold text-muted uppercase tracking-wider mb-2">Max Limit ($)</label>
                  <input
                    type="number"
                    readOnly
                    tabIndex={-1}
                    value={form.max_bet}
                    className="block w-full rounded-lg border-line shadow-sm sm:text-sm p-3 border bg-subtle text-muted cursor-not-allowed"
                  />
                  <p className="text-[11px] text-muted mt-1">Auto: Boot × 64</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-muted uppercase tracking-wider mb-2">Pot Limit ($)</label>
                  <input
                    type="number"
                    readOnly
                    tabIndex={-1}
                    value={derivePotLimitFromBoot(form.boot_amount)}
                    className="block w-full rounded-lg border-line shadow-sm sm:text-sm p-3 border bg-subtle text-muted cursor-not-allowed"
                  />
                  <p className="text-[11px] text-muted mt-1">Auto: Boot × 256</p>
                </div>
                <div>
                  <label className="block text-xs font-bold text-muted uppercase tracking-wider mb-2">Min Buy-in ($)</label>
                  <input
                    type="number"
                    required
                    min={form.boot_amount}
                    step={0.01}
                    value={form.min_buy_in}
                    onChange={(e) => setForm({ ...form, min_buy_in: Number(e.target.value) })}
                    className="block w-full rounded-lg border-line shadow-sm focus:border-brand-gold focus:ring-brand-gold sm:text-sm p-3 border"
                  />
                  <p className="text-[11px] text-muted mt-1">Minimum chips debited from wallet to join</p>
                </div>

                <div>
                  <label className="block text-xs font-bold text-muted uppercase tracking-wider mb-2">Shar Amount ($)</label>
                  <input
                    type="number"
                    required
                    min={0}
                    step={0.01}
                    value={form.shar_amount}
                    onChange={(e) => setForm({ ...form, shar_amount: Number(e.target.value) })}
                    className="block w-full rounded-lg border-line shadow-sm focus:border-brand-gold focus:ring-brand-gold sm:text-sm p-3 border"
                  />
                  <p className="text-[11px] text-muted mt-1">Optional pre-hand pot — 0 disables Shar</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-muted uppercase tracking-wider mb-2">Max Blinds</label>
                  <input
                    type="number"
                    required
                    min={1}
                    max={10}
                    value={form.max_blind_rounds}
                    onChange={(e) => setForm({ ...form, max_blind_rounds: Number(e.target.value) })}
                    className="block w-full rounded-lg border-line shadow-sm focus:border-brand-gold focus:ring-brand-gold sm:text-sm p-3 border"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-muted uppercase tracking-wider mb-2">Timeout action</label>
                  <select
                    value={form.timeout_action}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        timeout_action: e.target.value as TableTimeoutAction,
                      })
                    }
                    className="block w-full rounded-lg border-line shadow-sm focus:border-brand-gold focus:ring-brand-gold sm:text-sm p-3 border"
                  >
                    <option value="auto_pack">Auto pack (fold)</option>
                    <option value="auto_blind">Auto blind</option>
                    <option value="auto_chaal">Auto chaal</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-muted uppercase tracking-wider mb-2">Status</label>
                  <select
                    value={form.status ? 'true' : 'false'}
                    onChange={(e) => setForm({ ...form, status: e.target.value === 'true' })}
                    className="block w-full rounded-lg border-line shadow-sm focus:border-brand-gold focus:ring-brand-gold sm:text-sm p-3 border"
                  >
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 px-4 py-2.5 bg-subtle hover:bg-subtle text-body rounded-lg font-semibold border border-line transition-colors text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2.5 bg-brand-gold hover:bg-opacity-95 text-brand-dark rounded-lg font-bold transition-all text-sm shadow-sm"
                >
                  {isEditMode ? 'Save Changes' : 'Create Profile'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TableManagement;
